// Library Methods
import { useCallback, useEffect, useState, useMemo } from "react";

// Utils
import {
  isBinocular,
  isMonocularMultipleSections,
  calculateDurationForDisplay,
  isValidColorVisionReport,
  isValidVisualAcuityReport,
  isValidPelliRobsonReport,
  isValidVisualFieldReport,
  isValidCSTumblingEReport,
  isValidRAPDReport,
  getCompletedExamSections,
  isValidOcularMotilityReport,
  isValidLensSimulationReport,
  isValidVideoReport,
  isValidSynoptophoreReport,
  isValidAniseikoniaReport,
} from "src/utils/exams/examReportsHelper";

// Constants
import { ExamCardSectionNameType, eyeTypeEnums } from "src/constants/enums";

// Types
import {
  ExamReportDataType,
  ExamReportSectionType,
  ExamSectionType,
  ExamType,
} from "src/services/exams/types";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Hooks
import { useReportSectionData } from "src/services/exams/hooks/report-sections/useReportSectionData";
import { useReportSectionComments } from "src/services/exams/hooks/report-sections/useReportSectionComments";

type UseReportValidationType = {
  patientId: string;
  exam: ExamType | undefined;
};

const useReportValidation = ({ patientId, exam }: UseReportValidationType) => {
  const { clinicInfo } = useClinicContext();

  const { mutateAsync: reportSectionMutate } = useReportSectionData(
    clinicInfo?.id
  );
  const { mutateAsync: reportCommentsMutate } = useReportSectionComments(
    clinicInfo?.id
  );

  const [reportData, setReportData] = useState<ExamReportDataType>({});
  const [shouldDisplayReport, setShouldDisplayReport] = useState(false);
  const [isValidating, setIsValidating] = useState(true);

  const durationRes = useMemo(() => "00:00", []);

  // whether the report should be displayed or not
  const validateExamReports = useCallback(
    (reportToValidate: ExamReportDataType, exam: ExamType) => {
      const reportSections = Object.values(reportToValidate);
      if (!reportSections.length) {
        return { isValid: false, validatedReport: reportToValidate };
      }

      let reportToEdit = { ...reportToValidate };
      const completedExamSections = getCompletedExamSections(exam);

      completedExamSections.forEach((sectionName) => {
        const examSections = Object.values(
          reportToValidate?.[sectionName] ?? {}
        );

        examSections.forEach((examSection) => {
          const groupId = examSection?.groupId;
          const report = examSection?.examReport;
          const report2 = examSection?.examReport2;

          const isInvalidExam = Boolean(
            exam?.[sectionName]?.find(
              (section) =>
                section.groupId === groupId &&
                section.completionState === "Invalid"
            )
          );

          if (isInvalidExam) {
            // add display property to the report section
            reportToEdit = {
              ...reportToEdit,
              [sectionName]: {
                ...reportToEdit?.[sectionName],
                [groupId]: {
                  ...reportToEdit?.[sectionName]?.[groupId],
                  display: false,
                },
              },
            };
            return;
          }

          const isValidColorVision =
            (report && isValidColorVisionReport(report)) ||
            (report2 && isValidColorVisionReport(report2));

          const isValidVisualAcuity =
            (report && isValidVisualAcuityReport(report)) ||
            (report2 && isValidVisualAcuityReport(report2));

          const isValidPelliRobson =
            (report && isValidPelliRobsonReport(report)) ||
            (report2 && isValidPelliRobsonReport(report2));

          const isValidCSTumblingE =
            (report && isValidCSTumblingEReport(report)) ||
            (report2 && isValidCSTumblingEReport(report2));

          const isValidVisualField =
            (report && isValidVisualFieldReport(report)) ||
            (report2 && isValidVisualFieldReport(report2));

          const isValidRAPD =
            (report && isValidRAPDReport(report)) ||
            (report2 && isValidRAPDReport(report2));

          const isValidOcularMotility =
            (report && isValidOcularMotilityReport(report)) ||
            (report2 && isValidOcularMotilityReport(report2));

          const isValidSynoptophore =
            (report && isValidSynoptophoreReport(report)) ||
            (report2 && isValidSynoptophoreReport(report2));

          const isValidAniseikonia =
            (report && isValidAniseikoniaReport(report)) ||
            (report2 && isValidAniseikoniaReport(report2));

          const isValidLensSimulation =
            (report && isValidLensSimulationReport(report)) ||
            (report2 && isValidLensSimulationReport(report2));

          const isValidVideo =
            (report && isValidVideoReport(report)) ||
            (report2 && isValidVideoReport(report2));

          if (
            isValidColorVision ||
            isValidVisualAcuity ||
            isValidPelliRobson ||
            isValidCSTumblingE ||
            isValidVisualField ||
            isValidRAPD ||
            isValidOcularMotility ||
            isValidSynoptophore ||
            isValidAniseikonia ||
            isValidLensSimulation ||
            isValidVideo
          ) {
            // add display property to the report section
            reportToEdit = {
              ...reportToEdit,
              [sectionName]: {
                ...reportToEdit?.[sectionName],
                [groupId]: {
                  ...reportToEdit?.[sectionName]?.[groupId],
                  display: true,
                },
              },
            };
            return;
          }

          // add display property to the report section
          reportToEdit = {
            ...reportToEdit,
            [sectionName]: {
              ...reportToEdit?.[sectionName],
              [groupId]: {
                ...reportToEdit?.[sectionName]?.[groupId],
                display: false,
              },
            },
          };
          return;
        });
      });

      const formattedReport = Object.values(reportToEdit);
      const formattedSections = formattedReport
        .map((report) => Object.values(report))
        .flat();

      // if at least one section can be displayed, then the report is valid
      const isValid = formattedSections.some(
        (report) => report?.display === true
      );

      return { isValid, validatedReport: reportToEdit };
    },
    []
  );

  const getComments = useCallback(
    async (sectionId: string, examId: string) => {
      return reportCommentsMutate({
        patientId,
        examId,
        sectionId,
      });
    },
    [patientId, reportCommentsMutate]
  );

  const fetchReportSectionData = useCallback(
    async (sectionId: string, examId: string) => {
      try {
        const submission = await reportSectionMutate({
          patientId,
          examId,
          sectionId,
        });
        return JSON.parse(submission);
      } catch (e) {
        return undefined;
      }
    },
    [patientId, reportSectionMutate]
  );

  const fetchSingleExamSection = useCallback(
    async (
      examSections: Array<ExamSectionType>,
      sectionName: ExamCardSectionNameType,
      examId: string
    ) => {
      try {
        if (!examSections || !examSections?.length) return;

        const { LEFT, RIGHT } = eyeTypeEnums;

        let sectionData: { [groupId: number]: ExamReportSectionType } = {};
        const groupId = examSections?.[0]?.groupId;

        // check if the first section's eye matches the settings for displaying reports
        const displayEyeFirst =
          (examSections?.[0]?.eye === LEFT &&
            Boolean(clinicInfo?.settings?.reportDisplayLeftEyeFirst)) ||
          (examSections?.[0]?.eye === RIGHT &&
            !clinicInfo?.settings?.reportDisplayLeftEyeFirst);

        const shouldDoFirstSection =
          !isMonocularMultipleSections(examSections) ||
          isBinocular(examSections) ||
          displayEyeFirst;

        const sectionId: string = shouldDoFirstSection
          ? examSections?.[0]?.id
          : examSections?.[1]?.id;

        const completionDate = shouldDoFirstSection
          ? examSections?.[0]?.completionDate
          : examSections?.[1]?.completionDate;

        // get exam report 1
        if (completionDate) {
          const examReportContent1 = await fetchReportSectionData(
            sectionId,
            examId
          );

          if (examReportContent1) {
            const duration = calculateDurationForDisplay(
              examReportContent1?.timing?.elapsed
            );
            const durationStartTime =
              duration?.length > durationRes.length
                ? durationRes + ":00"
                : durationRes;

            let commentData = await getComments(sectionId, examId);
            commentData = commentData?.sort((a, b) =>
              b?.creationDate?.localeCompare(a?.creationDate)
            );

            const eyeType = shouldDoFirstSection
              ? examSections?.[0]?.eye
              : examSections?.[1]?.eye;

            sectionData = {
              [groupId]: {
                ...sectionData?.[groupId],
                groupId,
                sectionName,
                examReport: examReportContent1,
                commentData,
                eyeType,
                sectionId,
                duration,
                durationStartTime,
              },
            };
          }
        }

        // get exam report 2, if available
        if (isMonocularMultipleSections(examSections)) {
          const sectionId2 = shouldDoFirstSection
            ? examSections?.[1]?.id
            : examSections?.[0]?.id;

          const completionDate2 = shouldDoFirstSection
            ? examSections?.[1]?.completionDate
            : examSections?.[0]?.completionDate;

          if (completionDate2) {
            const examReportContent2 = await fetchReportSectionData(
              sectionId2,
              examId
            );

            if (examReportContent2) {
              const duration2 = calculateDurationForDisplay(
                examReportContent2?.timing?.elapsed
              );
              const durationStartTime2 =
                duration2?.length > durationRes.length
                  ? durationRes + ":00"
                  : durationRes;

              let commentData2 = await getComments(sectionId2, examId);
              commentData2 = commentData2?.sort((a, b) =>
                b?.creationDate?.localeCompare(a?.creationDate)
              );

              const eyeType2 = shouldDoFirstSection
                ? examSections?.[1]?.eye
                : examSections?.[0]?.eye;

              sectionData = {
                [groupId]: {
                  ...sectionData?.[groupId],
                  groupId,
                  sectionName,
                  examReport2: examReportContent2,
                  sectionId2,
                  commentData2,
                  eyeType2,
                  duration2,
                  durationStartTime2,
                },
              };
            }
          }
        }

        return sectionData;
      } catch (e) {
        throw e;
      }
    },
    [
      fetchReportSectionData,
      getComments,
      durationRes,
      clinicInfo?.settings?.reportDisplayLeftEyeFirst,
    ]
  );

  const refetchComment = useCallback(
    async (exam: ExamType) => {
      try {
        const completedExamSections = getCompletedExamSections(exam);

        // refetch comments for each exam section
        for (const sectionName of completedExamSections) {
          const examSections = Object.values(reportData?.[sectionName] ?? {});

          examSections.forEach(async (examSection) => {
            const groupId = examSection?.groupId;

            if (examSection?.sectionId) {
              let commentData = await getComments(
                examSection?.sectionId,
                exam.id
              );
              commentData = commentData?.sort((a, b) =>
                b?.creationDate?.localeCompare(a?.creationDate)
              );

              setReportData((prev: ExamReportDataType) => ({
                ...prev,
                [sectionName]: {
                  ...(prev?.[sectionName] ?? {}),
                  [groupId]: {
                    ...(prev?.[sectionName]?.[groupId] ?? {}),
                    commentData,
                  },
                },
              }));
            }

            if (examSection?.sectionId2) {
              let commentData2 = await getComments(
                examSection?.sectionId2,
                exam.id
              );
              commentData2 = commentData2?.sort((a, b) =>
                b?.creationDate?.localeCompare(a?.creationDate)
              );

              setReportData((prev: ExamReportDataType) => ({
                ...prev,
                [sectionName]: {
                  ...(prev?.[sectionName] ?? {}),
                  [groupId]: {
                    ...(prev?.[sectionName]?.[groupId] ?? {}),
                    commentData2,
                  },
                },
              }));
            }
          });
        }
      } catch (e) {
        // silent error handling
      }
    },
    [getComments, reportData]
  );

  // method that does the initial data fetching, showing the loading indicator
  const initialFetch = useCallback(
    async (examData: ExamType) => {
      try {
        const completedExamSections = getCompletedExamSections(examData);

        let completeReportData: ExamReportDataType = {};

        let formattedSections: {
          [key in ExamCardSectionNameType]?: {
            [groupId: number]: Array<ExamSectionType>;
          };
        } = {};

        // fetch information for each exam section
        const examPromises = completedExamSections.map(async (sectionName) => {
          // generate formatted exam sections
          examData[sectionName]?.forEach((section) => {
            const sectionsToFetch = examData[sectionName]?.filter(
              (toFetch) => section.groupId === toFetch.groupId
            );

            if (!formattedSections?.[sectionName]?.[section.groupId]) {
              formattedSections = {
                ...formattedSections,
                [sectionName]: {
                  ...formattedSections?.[sectionName],
                  [section.groupId]: sectionsToFetch,
                },
              };
            }
          });

          // fetch each section inside of a given sectionName
          const sectionArrays = Object.values(
            formattedSections[sectionName] ?? {}
          );
          const sectionPromises = sectionArrays.map((sectionArray) =>
            fetchSingleExamSection(sectionArray, sectionName, examData.id)
          );

          // wait for ALL sections to complete (including any retries)
          const sectionResults = await Promise.allSettled(sectionPromises);

          // process results and build complete reportData object
          sectionResults.forEach((result) => {
            if (result.status === "fulfilled" && result.value) {
              completeReportData[sectionName] = {
                ...completeReportData[sectionName],
                ...result.value,
              };
            }
          });
        });

        // wait for all report info to be fetched and formatted
        await Promise.allSettled(examPromises);

        // check if a valid report section exists
        const { isValid, validatedReport } = validateExamReports(
          completeReportData,
          examData
        );

        // update ALL state at once
        setReportData(validatedReport);
        setShouldDisplayReport(isValid);
      } catch {
        // Set shouldDisplayReport to false on error
        setShouldDisplayReport(false);
      } finally {
        setIsValidating(false);
      }
    },
    [validateExamReports, fetchSingleExamSection]
  );

  // initial data fetching
  useEffect(() => {
    if (exam) {
      initialFetch(exam);
    }
  }, [initialFetch, exam]);

  return {
    isValidating,
    reportData,
    shouldDisplayReport,
    refetchComment,
  };
};

export default useReportValidation;
